import React, { useState } from 'react';
import './MindMap.css';

// Faithful Claude-style mind-map: centered wordmark, organic asymmetric
// layout, thin connecting lines, plain typography, no boxes/badges/animations.
// All positions hand-laid for visual balance — no auto-layout.

// Center pushed slightly down because Engineering branch reaches way up high
// — keeps the visual weight balanced. Total viewBox height bumped to 1080 so
// the asymmetric branches have room to spread without colliding.
const CENTER = { x: 500, y: 540 };

// Each cluster: a category label + a few leaves around it. Positions are
// hand-tuned (not on a grid) to look organic. Crucially asymmetric in Y:
//   Engineering — sticks UP HIGH (top of canvas, ~y=40 leaves)
//   Quality     — mid-top, less reach than Engineering
//   Analysis    — sticks DOWN LOW
//   DevOps      — mid-bottom, less reach than Analysis
//   Documentation — bottom-center, lowest leaves
// The contrast (Engineering up high vs. Documentation way down) is what
// gives the chart its tall, organic feel — instead of a tidy donut.
//
// `link: true` on a leaf renders it underlined like a hyperlink (Claude
// underlines specific terms as visual interest — "Growth", "Debugging" etc).
const CLUSTERS = [
  {
    label: 'Engineering',
    cat: { x: 240, y: 235 },
    leaves: [
      { text: 'Refactoring',  x:  95, y: 145, link: true },
      { text: 'Bug fixes',    x: 175, y:  60 },         // way at the top
      { text: 'Migrations',   x: 380, y:  90 },
      { text: 'Optimization', x: 415, y: 200, link: true },
      { text: 'Code review',  x: 295, y: 305 },
    ],
  },
  {
    label: 'Quality',
    cat: { x: 770, y: 320 },
    leaves: [
      { text: 'E2E browser', x: 855, y: 200, link: true },
      { text: 'Unit',        x: 935, y: 280 },
      { text: 'Regression',  x: 920, y: 365 },
      { text: 'Visual diff', x: 700, y: 220 },
      { text: 'Performance', x: 660, y: 380 },
    ],
  },
  {
    label: 'Analysis',
    cat: { x: 200, y: 800 },
    leaves: [
      { text: 'Ticket triage', x:  90, y: 760, link: true },
      { text: 'Codebase Q&A',  x:  80, y: 855 },
      { text: 'Architecture',  x: 165, y: 920 },         // way down low
      { text: 'Tech debt',     x: 270, y: 905, link: true },
      { text: 'Dead code',     x: 310, y: 815 },
    ],
  },
  {
    label: 'DevOps',
    cat: { x: 800, y: 720 },
    leaves: [
      { text: 'CI/CD gates',     x: 715, y: 645 },
      { text: 'Deploy verify',   x: 880, y: 660, link: true },
      { text: 'Incident triage', x: 920, y: 750 },
      { text: 'Cost analysis',   x: 880, y: 825 },
      { text: 'Infra review',    x: 700, y: 810 },
    ],
  },
  {
    label: 'Documentation',
    cat: { x: 500, y: 945 },
    leaves: [
      { text: 'API docs',   x: 365, y: 970, link: true },
      { text: 'READMEs',    x: 445, y: 1025 },           // very bottom of canvas
      { text: 'Onboarding', x: 555, y: 1025 },
      { text: 'Changelogs', x: 640, y: 970 },
    ],
  },
];

const ZibbyMark = () => (
  // Just the wordmark — no logo. Centered.
  <text x={CENTER.x} y={CENTER.y + 10} className="mm-wordmark" textAnchor="middle">
    Teamy
  </text>
);

// Trim a line's endpoints inward by `padStart` (from x1,y1) and `padEnd`
// (from x2,y2) along its own direction. Used to keep lines from running
// into label bounding boxes — produces real whitespace around text.
function trimLine(x1, y1, x2, y2, padStart, padEnd) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.hypot(dx, dy);
  if (dist === 0) return { x1, y1, x2, y2 };
  const ux = dx / dist;
  const uy = dy / dist;
  return {
    x1: x1 + ux * padStart,
    y1: y1 + uy * padStart,
    x2: x2 - ux * padEnd,
    y2: y2 - uy * padEnd,
  };
}

// Padding values for the three label tiers — larger text needs larger gap.
const PAD_LEAF     = 26;   // small sans labels
const PAD_CATEGORY = 42;   // bold serif labels
const PAD_CENTER   = 78;   // large "Zibby" wordmark

// Tiny seeded PRNG so sketchy paths stay stable across renders (otherwise
// every keystroke would re-roll the wobble and the lines would jitter).
function rng(seed) {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return (s / 0x7fffffff) * 2 - 1; // -1..1
  };
}

// Hand-drawn line: quadratic bezier with a control point offset perpendicular
// to the line by a small wobble. Each line gets its own seed so wobbles vary.
function sketchyLinePath(x1, y1, x2, y2, seed) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.hypot(dx, dy);
  if (dist === 0) return `M ${x1},${y1} L ${x2},${y2}`;
  const r = rng(seed);
  const wobble = r() * Math.min(dist * 0.045, 14);
  const mx = (x1 + x2) / 2 + (-dy / dist) * wobble;
  const my = (y1 + y2) / 2 + (dx / dist) * wobble;
  return `M ${x1.toFixed(1)},${y1.toFixed(1)} Q ${mx.toFixed(1)},${my.toFixed(1)} ${x2.toFixed(1)},${y2.toFixed(1)}`;
}


const MindMap = () => {
  // Track which cluster is being hovered (by index). Hovering anything in
  // the cluster — category label or any leaf — activates the whole branch.
  const [hoveredCluster, setHoveredCluster] = useState(null);

  const enter = (ci) => () => setHoveredCluster(ci);
  const leave = () => setHoveredCluster(null);

  return (
    <svg className="mm-svg" viewBox="0 0 1000 1080" xmlns="http://www.w3.org/2000/svg">
      {/* Lines: trimmed at both ends, drawn as wobbly hand-sketched paths */}
      {CLUSTERS.map((cluster, ci) => {
        const trunk = trimLine(
          cluster.cat.x, cluster.cat.y,
          CENTER.x, CENTER.y,
          PAD_CATEGORY, PAD_CENTER,
        );
        const isActive = hoveredCluster === ci;
        return (
          <g key={`lines-${ci}`} className={isActive ? 'mm-cluster-active' : ''}>
            <path
              d={sketchyLinePath(trunk.x1, trunk.y1, trunk.x2, trunk.y2, 1000 + ci * 13)}
              className="mm-line"
            />
            {cluster.leaves.map((leaf, li) => {
              const branch = trimLine(
                leaf.x, leaf.y,
                cluster.cat.x, cluster.cat.y,
                PAD_LEAF, PAD_CATEGORY,
              );
              return (
                <path
                  key={li}
                  d={sketchyLinePath(branch.x1, branch.y1, branch.x2, branch.y2, ci * 100 + li * 7)}
                  className="mm-line"
                />
              );
            })}
          </g>
        );
      })}

      {/* Leaf labels — shake when their cluster is hovered */}
      {CLUSTERS.map((cluster, ci) =>
        cluster.leaves.map((leaf, li) => {
          const isActive = hoveredCluster === ci;
          return (
            <text
              key={`leaf-${ci}-${li}`}
              x={leaf.x} y={leaf.y}
              className={`mm-leaf ${leaf.link ? 'mm-leaf-link' : ''} ${isActive ? 'mm-leaf-shake' : ''}`}
              textAnchor="middle"
              dominantBaseline="middle"
              onMouseEnter={enter(ci)}
              onMouseLeave={leave}
              style={{
                transformOrigin: `${leaf.x}px ${leaf.y}px`,
                animationDelay: isActive ? `${li * 80}ms` : undefined,
              }}
            >
              {leaf.text}
            </text>
          );
        })
      )}

      {/* Category labels — scale up when active */}
      {CLUSTERS.map((cluster, ci) => {
        const isActive = hoveredCluster === ci;
        return (
          <text
            key={`cat-${ci}`}
            x={cluster.cat.x} y={cluster.cat.y}
            className={`mm-category ${isActive ? 'mm-category-active' : ''}`}
            textAnchor="middle"
            dominantBaseline="middle"
            onMouseEnter={enter(ci)}
            onMouseLeave={leave}
            style={{ transformOrigin: `${cluster.cat.x}px ${cluster.cat.y}px` }}
          >
            {cluster.label}
          </text>
        );
      })}

      {/* Center wordmark */}
      <ZibbyMark />
    </svg>
  );
};

export default MindMap;
